import React from 'react';
import { Icon } from '../brand/Icon.jsx';
import { Button } from './Button.jsx';

/**
 * File upload with drag-and-drop.
 *
 * NEW COMPONENT — not in the upstream design system. Built to its conventions
 * so it can be lifted back.
 *
 * Drag-and-drop is the affordance; the button is the path that actually works
 * for everyone. A dropzone with no button excludes keyboard users entirely.
 */
export function FileDrop({
  label,
  hint,
  error,
  accept = '.xlsx,.xls,.csv',
  maxSizeMb = 10,
  files = [],
  onFiles,
  onRemove,
  disabled = false,
  id,
  style,
}) {
  const reactId = React.useId();
  const dropId = id || reactId;
  const describedById = `${dropId}-desc`;
  const inputRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const [localError, setLocalError] = React.useState();

  const message = error || localError || hint;
  const isError = Boolean(error || localError);

  function accepted(list) {
    const out = [];
    for (const file of list) {
      if (file.size > maxSizeMb * 1024 * 1024) {
        setLocalError(`${file.name} is larger than ${maxSizeMb}MB.`);
        continue;
      }
      out.push(file);
    }
    return out;
  }

  function handleFiles(list) {
    setLocalError(undefined);
    const ok = accepted(Array.from(list ?? []));
    if (ok.length > 0) onFiles?.(ok);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', ...style }}>
      {label && (
        <span
          id={`${dropId}-label`}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-bold)',
            color: 'var(--text-strong)',
          }}
        >
          {label}
        </span>
      )}

      <div
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-8) var(--sp-5)',
          borderRadius: 'var(--radius-lg)',
          border: `1.5px dashed ${
            isError ? 'var(--y-danger)' : dragging ? 'var(--accent-primary)' : 'var(--border-default)'
          }`,
          background: dragging ? 'var(--y-blue-50)' : 'var(--bg-inset)',
          transition: 'border-color var(--dur) var(--ease-out), background var(--dur) var(--ease-out)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Icon name="upload" size={26} color="var(--text-muted)" />

        <p
          style={{
            margin: 0,
            textAlign: 'center',
            fontFamily: 'var(--font-body)',
            color: 'var(--text-body)',
          }}
        >
          Drag a spreadsheet here, or
        </p>

        <Button
          type="button"
          variant="secondary"
          disabled={disabled}
          data-y365-focusable
          onClick={() => inputRef.current?.click()}
        >
          Choose a file
        </Button>

        <input
          ref={inputRef}
          id={dropId}
          type="file"
          accept={accept}
          multiple
          disabled={disabled}
          aria-labelledby={label ? `${dropId}-label` : undefined}
          aria-describedby={message ? describedById : undefined}
          onChange={(e) => {
            handleFiles(e.target.files);
            // Reset so choosing the same file twice still fires onChange.
            e.target.value = '';
          }}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        />
      </div>

      {files.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-3) var(--sp-4)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-surface)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-sm)',
              }}
            >
              <Icon name="file-spreadsheet" size={18} color="var(--text-brand)" />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                {(file.size / 1024).toFixed(0)} KB
              </span>
              {onRemove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-y365-focusable
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onRemove(index)}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {message && (
        <span
          id={describedById}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-xs)',
            color: isError ? 'var(--y-danger)' : 'var(--text-muted)',
          }}
        >
          {message}
        </span>
      )}
    </div>
  );
}
